'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { factoryApi } from '@/lib/api';
import {
  Factory, Globe, Copy, Rocket, Loader2, Link2, CheckCircle2, Circle,
  AlertCircle, ChevronDown, ChevronRight, ExternalLink, Clock, Search,
  FileText, RefreshCw, Pause, X, Building2, Phone, Mail, MapPin,
} from 'lucide-react';
import { usePersistedState } from '@/lib/usePersistedState';
import { ProjectAccountBadge, WalkingStrip, useLiveRuns } from '@/components/pipeline/PipelinePicker';
import { PipelineDoc } from '@/components/pipeline/PipelineDoc';
import { IntakeChat } from '@/components/pipeline/IntakeChat';
import { SeedGate } from '@/components/pipeline/SeedGate';
import { RunScreen } from '@/components/pipeline/RunScreen';

/* ── Types ── */

type BuildMode = 'scratch' | 'clone' | 'template';
type ContentStrategy = 'research' | 'rewrite' | 'hybrid';
type MarketScope = 'local' | 'city' | 'province' | 'country';

interface BusinessInfo {
  name: string; phone: string; email: string;
  address: string; city: string; province: string;
}

interface BuildPhase {
  name: string;
  status: 'pending' | 'running' | 'done' | 'error';
  agent?: string;
  startedAt?: string;
  completedAt?: string;
  details?: string;
  progress?: number;
}

interface StepLogEntry {
  step: number; label: string; phase: string;
  timestamp: string; status: 'done' | 'running' | 'error';
}

interface BuildStatus {
  domain: string;
  mode?: BuildMode;
  status: 'running' | 'done' | 'error' | 'complete' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  phases: BuildPhase[];
  buildLog?: StepLogEntry[];
  error?: string;
  previewUrl?: string;
  orchestratorAlive?: boolean;
}

/* ── Constants ── */

const EMPTY_BIZ: BusinessInfo = { name: '', phone: '', email: '', address: '', city: '', province: '' };

const PROVINCES = [
  '', 'ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'NT', 'YT', 'NU',
];
const PROVINCE_LABELS: Record<string, string> = {
  '': 'Select...', ON: 'Ontario', QC: 'Quebec', BC: 'British Columbia',
  AB: 'Alberta', MB: 'Manitoba', SK: 'Saskatchewan', NS: 'Nova Scotia',
  NB: 'New Brunswick', NL: 'Newfoundland', PE: 'PEI', NT: 'NWT', YT: 'Yukon', NU: 'Nunavut',
};

const MARKET_SCOPES: { value: MarketScope; label: string; desc: string; icon: string }[] = [
  { value: 'local', label: 'Local', desc: 'Neighborhood', icon: '📍' },
  { value: 'city', label: 'City', desc: 'City + suburbs', icon: '🏙️' },
  { value: 'province', label: 'Province', desc: 'Province-wide', icon: '🗺️' },
  { value: 'country', label: 'Country', desc: 'Nationwide', icon: '🇨🇦' },
];

const PHASE_ESTIMATES: Record<string, number> = {
  'Clone Vision': 5, 'Clone Extraction': 3, 'SEO Research': 8, 'SEO Audit': 5,
  'Engine Setup': 12, 'Content Writing': 10, 'Image Sourcing': 8,
  'UI Design': 25, 'Clone UI': 20, 'Design Review': 10,
  'Quality Assurance': 8, 'Preview Ready': 2,
};

/* ── Helpers ── */

function elapsed(start: string, end?: string): string {
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const input = 'w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 disabled:opacity-50 text-sm';
const smallInput = 'w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600 disabled:opacity-50';

/* ── Component ── */

export default function FactoryPage() {
  // Persisted state — survives navigation
  // Brief comes FIRST — it is Stage -1, and it is where Boss declares the things
  // FACTORY-MASTER's 1136 judgement checks keep saying 'Boss declares'.
  // Order IS the flow: declare -> approve the seeds -> build -> watch.
  // 'run' is THE screen: start it, answer as it goes, scroll back through
  // everything. The other views are detail, not the flow.
  const [view, setView] = usePersistedState<'run' | 'brief' | 'seeds' | 'build' | 'pipeline'>('factory:view', 'brief');
  const [briefProject, setBriefProjectRaw] = useState<{ slug: string; path: string } | null>(null);
  // Remember the project across reloads and across tabs. Without this the page
  // reopened on rows[0] every time — Boss kept landing on Graffitiremoval while
  // his actual run was waiting on another project.
  const [lastSlug, setLastSlug] = usePersistedState('factory:project', '');
  const [allProjects, setAllProjects] = useState<{ slug: string; name: string; path: string }[]>([]);
  // Which projects the run-loop is working RIGHT NOW — shown inside the
  // project dropdown itself (Boss, 2026-09-01: "I should be able to see which
  // ones are working right now"). Native <option> takes text only, so the
  // state rides in the label.
  const liveRuns = useLiveRuns(allProjects);
  const optLabel = (p: { slug: string; name: string }) =>
    liveRuns[p.slug] === 'running' ? `● ${p.name} — running`
      : liveRuns[p.slug] === 'waiting' ? `◔ ${p.name} — waiting on you`
      : p.name;

  useEffect(() => {
    fetch('/api/projects').then(r => r.json()).then(d => {
      const rows = (Array.isArray(d) ? d : d.projects || [])
        .filter((p: any) => p?.path && p?.slug)
        .map((p: any) => ({ slug: p.slug, name: p.name || p.slug, path: p.path }));
      setAllProjects(rows);
      // Read localStorage DIRECTLY rather than using lastSlug here.
      // usePersistedState starts at its default and hydrates in an effect, so
      // this effect's closure sees '' on mount — a race that made the picker
      // silently fall back to rows[0] every single time. Measured, not guessed:
      // select ADUs -> reload -> back to graffitiremoval-ca.
      let saved = '';
      try { saved = JSON.parse(localStorage.getItem('beast:factory:project') || '""'); }
      catch { saved = ''; }
      setBriefProjectRaw(prev => prev
        ?? rows.find((r: any) => r.slug === saved)        // where he left off
        ?? rows[0] ?? null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One setter so every picker persists — three tabs share this project, and a
  // picker that forgets is worse than no picker.
  const setBriefProject = useCallback((p: { slug: string; path: string } | null) => {
    setBriefProjectRaw(p);
    setLastSlug(p?.slug || '');
  }, [setLastSlug]);
  const [mode, setMode] = usePersistedState<BuildMode>('factory:mode', 'scratch');
  const [contentStrategy, setContentStrategy] = usePersistedState<ContentStrategy>('factory:strategy', 'research');
  const [marketScope, setMarketScope] = usePersistedState<MarketScope>('factory:scope', 'local');
  const [primaryArea, setPrimaryArea] = usePersistedState('factory:area', '');
  const [topic, setTopic] = usePersistedState('factory:topic', '');
  const [domain, setDomain] = usePersistedState('factory:domain', '');
  const [referenceUrl, setReferenceUrl] = usePersistedState('factory:refUrl', '');
  const [clientSiteUrl, setClientSiteUrl] = usePersistedState('factory:clientUrl', '');
  const [activeDomain, setActiveDomain] = usePersistedState<string | null>('factory:activeBuild', null);
  const [stepsOpen, setStepsOpen] = usePersistedState('factory:stepsOpen', true);

  // Ephemeral state — OK to reset
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({ ...EMPTY_BIZ });
  const [showBizInfo, setShowBizInfo] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildStatus, setBuildStatus] = useState<BuildStatus | null>(null);
  const [allBuilds, setAllBuilds] = useState<BuildStatus[]>([]);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const loadedRef = useRef(false);

  // Load all builds on mount
  useEffect(() => {
    factoryApi.status().then((data: any) => {
      const builds = (data.builds || []) as BuildStatus[];
      setAllBuilds(builds);
      if (!loadedRef.current && builds.length > 0) {
        loadedRef.current = true;

        // If we have a persisted activeDomain, restore that build
        if (activeDomain) {
          const persisted = builds.find(b => (b.domain || '').replace(/\./g, '-') === activeDomain);
          if (persisted) {
            setBuildStatus(persisted);
            if (persisted.status === 'running' && persisted.orchestratorAlive !== false) setBuilding(true);
            return;
          }
        }

        // Otherwise pick running build or most recent
        const running = builds.find(b => b.status === 'running');
        const show = running || builds[0];
        if (show) {
          const slug = (show.domain || '').replace(/\./g, '-');
          setActiveDomain(slug);
          setBuildStatus(show);
          if (running && running.orchestratorAlive !== false) setBuilding(true);
        }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll active build
  useEffect(() => {
    if (!activeDomain) return;
    const poll = () => {
      factoryApi.status(activeDomain).then((data: any) => {
        if (data.error) return;
        setBuildStatus(data);
        if (['done', 'complete', 'error', 'cancelled'].includes(data.status)) setBuilding(false);
        if (data.status === 'running' && data.orchestratorAlive === false) setBuilding(false);
      }).catch(() => {});
    };
    poll();
    pollRef.current = setInterval(poll, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeDomain]);

  const handleBuild = useCallback(async () => {
    if (!domain.trim()) return;
    setBuilding(true);
    const slug = domain.trim().replace(/\./g, '-');
    setActiveDomain(slug);
    setBuildStatus(null);
    try {
      const data = await factoryApi.build({
        domain: domain.trim(), mode, contentStrategy, marketScope,
        primaryArea: primaryArea.trim() || undefined,
        topic: topic.trim() || undefined,
        referenceUrl: referenceUrl.trim() || undefined,
        clientSiteUrl: clientSiteUrl.trim() || undefined,
        businessInfo: Object.values(businessInfo).some(v => v.trim()) ? businessInfo : undefined,
      });
      if (!data.ok) { setBuilding(false); setActiveDomain(null); }
    } catch { setBuilding(false); setActiveDomain(null); }
  }, [domain, mode, contentStrategy, marketScope, primaryArea, topic, referenceUrl, clientSiteUrl, businessInfo]);

  const handleCancel = useCallback(async () => {
    if (!activeDomain) return;
    await factoryApi.deleteBuild(activeDomain).catch(() => {});
    setBuilding(false);
  }, [activeDomain]);

  const handleResume = useCallback(async () => {
    if (!activeDomain) return;
    setBuilding(true);
    const data = await factoryApi.resume(activeDomain).catch(() => null);
    if (!data?.ok) setBuilding(false);
  }, [activeDomain]);

  /* ── Derived state ── */
  const isComplete = buildStatus?.status === 'done' || buildStatus?.status === 'complete';
  const isError = buildStatus?.status === 'error' || buildStatus?.status === 'cancelled';
  const isStale = buildStatus?.status === 'running' && buildStatus?.orchestratorAlive === false;
  const phasesCompleted = buildStatus?.phases?.filter(p => p.status === 'done').length || 0;
  const phasesTotal = buildStatus?.phases?.length || 0;
  const canResume = (isError || isStale) && buildStatus?.phases?.some(p => p.status === 'pending' || p.status === 'error');
  const canBuild = domain.trim() && !building;

  // Header + Build|Pipeline switch, shared by both views.
  const chrome = (subtitle: string) => (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-600">
          <Factory className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Website Factory</h1>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex gap-2 mb-6">
        {/* Brief FIRST in the strip too — Boss (2026-08-31): "first is the brief,
            then is the run". The tab order must read as the flow. */}
        {(['brief', 'run', 'seeds', 'build'] as const).map(v => (
          <button key={v} onClick={() => setView(v)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              view === v
                ? 'bg-white/10 text-white ring-1 ring-white/20'
                : 'text-gray-400 hover:text-white'}`}>
            {v}
          </button>
        ))}
      </div>
    </>
  );

  // Early return keeps the 340-line build form untouched — no structural surgery on a
  // JSX tree that already works and renders with zero console errors.
  if (view === 'brief') {
    return (
      <div className="p-8 max-w-6xl">
        {chrome('Tell Beast what to build — before any research starts')}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Project</span>
          <select value={briefProject?.slug || ''}
            onChange={e => setBriefProject(allProjects.find(p => p.slug === e.target.value) || null)}
            className="bg-white/5 text-gray-200 text-xs rounded-lg px-3 py-1.5
                       ring-1 ring-white/10 focus:outline-none focus:ring-cyan-500/40">
            {allProjects.length === 0 && <option value="">loading…</option>}
            {allProjects.map(p => (
              <option key={p.slug} value={p.slug} className="bg-[#0b0d12]">{optLabel(p)}</option>
            ))}
          </select>
        </div>
        {briefProject
          ? <IntakeChat key={briefProject.slug} slug={briefProject.slug} path={briefProject.path}
                        onLaunched={() => setView('run')} />
          : <div className="text-xs text-gray-600">Select a project.</div>}
      </div>
    );
  }

  // 'pipeline' was a separate tab until 2026-08-31 — Boss: "can't we combine Run
  // and Pipeline... merge them together to show both information?" One tab now:
  // walking strip + launch + stage progress, FACTORY-MASTER document below.
  // ('pipeline' stays accepted here so a stale persisted view lands on the
  // merged tab instead of falling through to the build form.)
  if (view === 'run' || view === 'pipeline') {
    return (
      <div className="p-8 max-w-6xl">
        {chrome('Start it. It asks you what it needs, as it needs it.')}
        <WalkingStrip />
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Project</span>
          <select value={briefProject?.slug || ''}
            onChange={e => setBriefProject(allProjects.find(p => p.slug === e.target.value) || null)}
            className="bg-white/5 text-gray-200 text-xs rounded-lg px-3 py-1.5
                       ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40">
            {allProjects.length === 0 && <option value="">loading…</option>}
            {allProjects.map(p => (
              <option key={p.slug} value={p.slug} className="bg-[#0b0d12]">{optLabel(p)}</option>
            ))}
          </select>
          {briefProject && <ProjectAccountBadge path={briefProject.path} />}
        </div>
        {briefProject
          ? (
            <>
              <RunScreen key={briefProject.slug} slug={briefProject.slug} path={briefProject.path}
                         onBrief={() => setView('brief')} />
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                  Where this project is inside FACTORY-MASTER.md
                </div>
                <PipelineDoc key={`doc-${briefProject.slug}`} slug={briefProject.slug} path={briefProject.path} />
              </div>
            </>
          )
          : <div className="text-xs text-gray-600">Select a project.</div>}
      </div>
    );
  }

  if (view === 'seeds') {
    return (
      <div className="p-8 max-w-5xl">
        {chrome('Approve the seeds before any research spends money')}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Project</span>
          <select value={briefProject?.slug || ''}
            onChange={e => setBriefProject(allProjects.find(p => p.slug === e.target.value) || null)}
            className="bg-white/5 text-gray-200 text-xs rounded-lg px-3 py-1.5
                       ring-1 ring-white/10 focus:outline-none focus:ring-cyan-500/40">
            {allProjects.length === 0 && <option value="">loading…</option>}
            {allProjects.map(p => (
              <option key={p.slug} value={p.slug} className="bg-[#0b0d12]">{optLabel(p)}</option>
            ))}
          </select>
        </div>
        {briefProject
          ? <SeedGate key={briefProject.slug} slug={briefProject.slug} path={briefProject.path} />
          : <div className="text-xs text-gray-600">Select a project.</div>}
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      {chrome('Build SEO-optimized bilingual websites')}

      {/* Mode */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {([
          { v: 'scratch' as BuildMode, icon: Globe, label: 'From Scratch', desc: 'Full creative control. Every agent builds from zero.', color: 'cyan' },
          { v: 'template' as BuildMode, icon: Rocket, label: 'Template', desc: 'Pre-built engine. Skips Forge. ~60% faster.', color: 'emerald' },
          { v: 'clone' as BuildMode, icon: Copy, label: 'Clone UI', desc: 'Clone a reference site\'s visual design.', color: 'orange' },
        ] as const).map(m => (
          <button key={m.v} onClick={() => setMode(m.v)} disabled={building}
            className={`p-5 rounded-xl border text-left transition-all ${
              mode === m.v ? `border-${m.color}-500/50 bg-${m.color}-500/10` : 'border-white/[0.08] bg-white/[0.04] hover:border-gray-700'
            } disabled:opacity-50`}>
            <m.icon className={`h-5 w-5 mb-2 ${mode === m.v ? `text-${m.color}-400` : 'text-gray-500'}`} />
            <p className={`text-sm font-bold mb-1 ${mode === m.v ? `text-${m.color}-400` : 'text-gray-300'}`}>{m.label}</p>
            <p className="text-[11px] text-gray-500">{m.desc}</p>
          </button>
        ))}
      </div>

      {/* Content Strategy */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">Content Strategy</label>
        <div className="flex gap-2">
          {([
            { v: 'research' as ContentStrategy, icon: Search, label: 'Research', desc: 'Write from scratch via keyword research' },
            { v: 'rewrite' as ContentStrategy, icon: FileText, label: 'Rewrite', desc: 'Scrape & rewrite client\'s existing site' },
            { v: 'hybrid' as ContentStrategy, icon: RefreshCw, label: 'Hybrid', desc: 'Scrape existing + research gaps' },
          ] as const).map(s => (
            <button key={s.v} onClick={() => setContentStrategy(s.v)} disabled={building}
              className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all ${
                contentStrategy === s.v ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-white/[0.08] bg-white/[0.04] hover:border-gray-700'
              } disabled:opacity-50`}>
              <s.icon className={`h-4 w-4 shrink-0 ${contentStrategy === s.v ? 'text-emerald-400' : 'text-gray-500'}`} />
              <div>
                <p className={`text-xs font-bold ${contentStrategy === s.v ? 'text-emerald-400' : 'text-gray-300'}`}>{s.label}</p>
                <p className="text-[10px] text-gray-500">{s.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Domain</label>
          <input type="text" value={domain} onChange={e => setDomain(e.target.value)} disabled={building}
            placeholder="yourdomain.ca" className={input} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Topic / Niche</label>
          <input type="text" value={topic} onChange={e => setTopic(e.target.value)} disabled={building}
            placeholder="e.g., Commercial bike rack installation" className={input} />
        </div>

        {/* Market Scope */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Market Scope</label>
          <div className="grid grid-cols-4 gap-2">
            {MARKET_SCOPES.map(s => (
              <button key={s.value} onClick={() => setMarketScope(s.value)} disabled={building}
                className={`p-2.5 rounded-lg border text-left transition-all ${
                  marketScope === s.value ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400' : 'border-white/[0.08] bg-white/[0.04] text-gray-400 hover:border-gray-700'
                } disabled:opacity-50`}>
                <span className="text-base">{s.icon}</span>
                <p className="text-xs font-semibold mt-1">{s.label}</p>
                <p className="text-[10px] text-gray-500">{s.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Primary Area</label>
          <input type="text" value={primaryArea} onChange={e => setPrimaryArea(e.target.value)} disabled={building}
            placeholder="e.g., Greater Toronto Area" className={input} />
        </div>

        {/* Reference URL */}
        {(mode === 'clone' || contentStrategy === 'research') && (
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
              <Link2 className="h-3.5 w-3.5" /> Reference Website <span className="text-gray-600 font-normal">(optional)</span>
            </label>
            <input type="url" value={referenceUrl} onChange={e => setReferenceUrl(e.target.value)} disabled={building}
              placeholder={mode === 'clone' ? 'https://competitor-site.com' : 'https://old-site.com'}
              className={input} />
          </div>
        )}

        {/* Client Site URL */}
        {contentStrategy !== 'research' && (
          <div>
            <label className="text-sm font-medium text-emerald-400 mb-1 flex items-center gap-2">
              <Globe className="h-3.5 w-3.5" /> Client&apos;s Existing Website
            </label>
            <input type="url" value={clientSiteUrl} onChange={e => setClientSiteUrl(e.target.value)} disabled={building}
              placeholder="https://existing-client-site.com" className={input} />
          </div>
        )}

        {/* Business Info (collapsible) */}
        <div className="glass-card overflow-hidden">
          <button onClick={() => setShowBizInfo(!showBizInfo)} disabled={building}
            className="flex items-center gap-2 w-full text-left px-4 py-3 hover:bg-white/[0.02] disabled:opacity-50">
            <Building2 className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-400">Business Details</span>
            <span className="text-[10px] text-gray-600">(optional)</span>
            {showBizInfo ? <ChevronDown className="h-3.5 w-3.5 text-gray-500 ml-auto" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-500 ml-auto" />}
          </button>
          {showBizInfo && (
            <div className="px-4 pb-4 border-t border-white/[0.04] grid grid-cols-2 gap-3 pt-3">
              {([
                { key: 'name' as const, icon: Building2, label: 'Business Name', ph: 'Acme Inc.' },
                { key: 'phone' as const, icon: Phone, label: 'Phone', ph: '(416) 555-0100' },
                { key: 'email' as const, icon: Mail, label: 'Email', ph: 'info@domain.ca' },
                { key: 'address' as const, icon: MapPin, label: 'Address', ph: '123 Main St' },
                { key: 'city' as const, icon: MapPin, label: 'City', ph: 'Toronto' },
              ]).map(f => (
                <div key={f.key}>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 mb-1"><f.icon className="h-3 w-3" />{f.label}</label>
                  <input type="text" value={businessInfo[f.key]}
                    onChange={e => setBusinessInfo(prev => ({ ...prev, [f.key]: e.target.value }))}
                    disabled={building} placeholder={f.ph} className={smallInput} />
                </div>
              ))}
              <div>
                <label className="flex items-center gap-1.5 text-xs text-gray-500 mb-1"><MapPin className="h-3 w-3" />Province</label>
                <select value={businessInfo.province}
                  onChange={e => setBusinessInfo(prev => ({ ...prev, province: e.target.value }))}
                  disabled={building} className={smallInput}>
                  {PROVINCES.map(p => <option key={p} value={p}>{PROVINCE_LABELS[p]}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Build Button */}
        <button onClick={handleBuild} disabled={!canBuild}
          className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-bold text-sm transition-all ${
            canBuild ? 'bg-cyan-500 text-black hover:bg-cyan-400' : 'bg-gray-800 text-gray-500 cursor-not-allowed'
          }`}>
          {building
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Building...</>
            : <><Rocket className="h-4 w-4" /> Build Website</>}
        </button>

        {/* Pause / Cancel */}
        {building && (
          <div className="flex gap-2">
            <button onClick={handleCancel}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20">
              <X className="h-4 w-4" /> Cancel Build
            </button>
          </div>
        )}

        {/* Resume */}
        {!building && canResume && (
          <button onClick={handleResume}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm bg-amber-500 text-black hover:bg-amber-400">
            <RefreshCw className="h-4 w-4" /> Resume Pipeline ({phasesCompleted}/{phasesTotal} done)
          </button>
        )}
      </div>

      {/* Build Selector */}
      {allBuilds.length > 1 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {allBuilds.slice(0, 10).map((b, i) => {
            const slug = (b.domain || '').replace(/\./g, '-');
            const isOrcDead = b.status === 'running' && b.orchestratorAlive === false;
            return (
              <button key={`${slug}-${i}`} onClick={() => { setActiveDomain(slug); setBuildStatus(b); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeDomain === slug
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-white/[0.05] text-gray-400 border border-white/[0.08] hover:border-gray-700'
                }`}>
                {b.domain?.replace(/-/g, '.') || slug}
                <span className={`ml-2 w-2 h-2 rounded-full inline-block ${
                  isOrcDead ? 'bg-amber-500' :
                  b.status === 'running' ? 'bg-cyan-500 animate-pulse' :
                  b.status === 'complete' || b.status === 'done' ? 'bg-green-500' : 'bg-red-500'
                }`} />
              </button>
            );
          })}
        </div>
      )}

      {/* Build Progress */}
      {buildStatus && (
        <div className="glass-card overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/[0.04]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white">{buildStatus.domain?.replace(/-/g, '.')} — Build Progress</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                isComplete ? 'bg-green-500/20 text-green-400' :
                isStale ? 'bg-amber-500/20 text-amber-400' :
                isError ? 'bg-red-500/20 text-red-400' :
                'bg-cyan-500/20 text-cyan-400 animate-pulse'
              }`}>
                {isComplete ? '✓ Complete' : isStale ? '⚠ Stale' : isError ? '✗ Error' : '● Running'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${
                  isComplete ? 'bg-green-500' : isStale ? 'bg-amber-500' : isError ? 'bg-red-500' : 'bg-cyan-500'
                }`} style={{ width: `${phasesTotal > 0 ? (phasesCompleted / phasesTotal) * 100 : 0}%` }} />
              </div>
              <span className="text-xs text-gray-500">{phasesCompleted}/{phasesTotal}</span>
              {buildStatus.startedAt && (
                <span className="text-xs text-gray-600 flex items-center gap-1">
                  <Clock className="h-3 w-3" />{elapsed(buildStatus.startedAt, buildStatus.completedAt)}
                </span>
              )}
            </div>
          </div>

          {/* Phases */}
          <div className="p-4 space-y-0.5">
            {buildStatus.phases?.map((phase, i) => (
              <div key={i} className={`flex items-start gap-3 py-2.5 px-3 rounded-lg ${phase.status === 'running' ? 'bg-cyan-500/5' : ''}`}>
                <div className="mt-0.5 shrink-0">
                  {phase.status === 'done' ? <CheckCircle2 className="h-5 w-5 text-green-400" /> :
                   phase.status === 'running' ? <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" /> :
                   phase.status === 'error' ? <AlertCircle className="h-5 w-5 text-red-400" /> :
                   <Circle className="h-5 w-5 text-gray-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        phase.status === 'running' ? 'text-cyan-400' : phase.status === 'done' ? 'text-green-400' :
                        phase.status === 'error' ? 'text-red-400' : 'text-gray-500'
                      }`}>{phase.name}</span>
                      {phase.agent && <span className="text-xs text-gray-600">{phase.agent}</span>}
                    </div>
                    {phase.completedAt && (
                      <span className="text-xs text-gray-600">
                        {new Date(phase.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  {phase.details && <p className="text-xs text-gray-500 mt-1">{phase.details}</p>}
                  {phase.status === 'running' && (() => {
                    const est = PHASE_ESTIMATES[phase.name] || 10;
                    const mins = phase.startedAt ? (Date.now() - new Date(phase.startedAt).getTime()) / 60000 : 0;
                    const pct = phase.progress || Math.min(95, Math.round((mins / est) * 100));
                    return (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden max-w-[200px]">
                          <div className="h-full bg-cyan-500/60 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-600">{pct}%</span>
                        {phase.startedAt && <span className="text-[10px] text-gray-600">{elapsed(phase.startedAt)}</span>}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>

          {/* Build Log */}
          {buildStatus.buildLog && buildStatus.buildLog.length > 0 && (
            <div className="mx-5 mb-4">
              <button onClick={() => setStepsOpen(!stepsOpen)}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300 mb-2">
                {stepsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Build Log ({buildStatus.buildLog.length})
              </button>
              {stepsOpen && (
                <div className="space-y-0.5 pl-2 border-l border-white/[0.08] max-h-60 overflow-y-auto">
                  {buildStatus.buildLog.map((entry, i) => (
                    <div key={i} className="flex items-start gap-2 pl-3 py-1">
                      {entry.status === 'done' ? <CheckCircle2 className="h-3 w-3 text-green-500/70 mt-0.5" /> :
                       entry.status === 'running' ? <Loader2 className="h-3 w-3 text-cyan-400 animate-spin mt-0.5" /> :
                       <AlertCircle className="h-3 w-3 text-red-400 mt-0.5" />}
                      <span className="text-xs text-gray-500 flex-1">{entry.label}</span>
                      <span className="text-xs text-gray-700 shrink-0">
                        {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {isStale && (
            <div className="mx-5 mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400 font-medium">⚠ Orchestrator process died — resume to continue</p>
            </div>
          )}
          {isError && !isStale && buildStatus.error && (
            <div className="mx-5 mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400">{buildStatus.error}</p>
            </div>
          )}

          {/* Preview Link */}
          {isComplete && buildStatus.previewUrl && (
            <div className="mx-5 mb-4 px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-sm text-green-400 font-medium">Website ready!</span>
              </div>
              <a href={buildStatus.previewUrl} target="_blank" rel="noopener noreferrer"
                className="px-4 py-1.5 rounded-lg bg-green-500 text-black text-xs font-bold hover:bg-green-400 flex items-center gap-1.5">
                Open Preview <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {buildStatus.completedAt && (
            <div className="px-5 py-3 border-t border-white/[0.04] text-xs text-gray-500">
              Completed {new Date(buildStatus.completedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Pipeline Overview (when no build active) */}
      {!buildStatus && !building && (
        <div className="p-5 glass-card">
          <h3 className="text-sm font-bold text-gray-300 mb-3">Pipeline — {mode === 'clone' ? '9' : '8'} Phases</h3>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {(mode === 'clone'
              ? ['🦾 Extract', '📊 SEO', '🔎 Audit', '⚙️ Engine', '✍️ Content', '📸 Images', '🪞 Clone', '👁️ Review', '🛡️ QA', '🚀 Preview']
              : ['📊 SEO', '🔎 Audit', '⚙️ Engine', '✍️ Content', '📸 Images', '🎨 Design', '👁️ Review', '🛡️ QA', '🚀 Preview']
            ).map((name, i, arr) => (
              <span key={name} className="flex items-center gap-1">
                <span className="px-2.5 py-1 rounded-md bg-[rgb(var(--muted))]/80 text-gray-400">{name}</span>
                {i < arr.length - 1 && <span className="text-gray-700">→</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
